import { getBearerToken } from "../../../../api/postCall/bearerToken";
import {
  searchForMedicalPaOrdersFromPostgres,
  SearchForMedicalPaOrdersFromPostgresParams,
} from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { AuthStatusOptionModel } from "../../../../data-model/authStatusOptions";
import { PostgresResponseForMedicalPaOrders } from "../../../../data-model/postgresResponseForMedicalPaOrders";
import { GenericAuthStatusTableData } from "../../../../data-model/tablesData";
import { GenericAuthStatusState } from "../hooks/useGenericAuthStatusTableData";
import { generateGenericAuthStatusTableDataFromAlgoliaRecords } from "../utils/generateTableDataFromAlgoliaRecords";

/**
 * Fetches initial table data by sequentially executing multiple paginated Postgres queries.
 * Combines all page responses into a single dataset and updates both API state and table data.
 *
 * @param payloads - Array of search parameter objects, one per page to fetch
 * @param authStatusOptions - Auth status options for formatting table badge data
 * @param setGenericAuthStatusState - State setter for API response metadata
 * @param setTableData - State setter for the processed table rows
 * @param setInitialDataFetchingExecuted - Flag setter to indicate initial fetch is complete
 */
export const makePostCallForInitialDataFetch = async (
  payloads: SearchForMedicalPaOrdersFromPostgresParams[],
  authStatusOptions: AuthStatusOptionModel[],
  setGenericAuthStatusState: React.Dispatch<
    React.SetStateAction<GenericAuthStatusState>
  >,
  setTableData: React.Dispatch<
    React.SetStateAction<GenericAuthStatusTableData[]>
  >,
  setInitialDataFetchingExecuted: React.Dispatch<React.SetStateAction<boolean>>,
) => {
  setGenericAuthStatusState((prev) => ({ ...prev, loading: true }));

  try {
    await getBearerToken();

    const data: PostgresResponseForMedicalPaOrders[] = [];

    for (const payload of payloads) {
      const response = await searchForMedicalPaOrdersFromPostgres(payload);
      const parsedResponse = JSON.parse(
        response,
      ) as PostgresResponseForMedicalPaOrders;
      data.push(parsedResponse);
    }

    const combinedData = appendAllResponses(data);

    setGenericAuthStatusState((prev) => ({
      ...prev,
      data: combinedData,
    }));
    setTableData(
      generateGenericAuthStatusTableDataFromAlgoliaRecords(
        combinedData.hits ?? [],
        authStatusOptions,
      ),
    );
  } catch (error) {
    setGenericAuthStatusState((prev) => ({ ...prev, error: error as string }));
  } finally {
    setGenericAuthStatusState((prev) => ({ ...prev, loading: false }));
    setInitialDataFetchingExecuted(true);
  }
};

/**
 * Merges multiple paginated Postgres responses into a single response by
 * concatenating all hits arrays while preserving the last response's metadata.
 *
 * @param responses - Array of paginated Postgres response objects
 * @returns Single combined response with all hits merged
 */
const appendAllResponses = (
  responses: PostgresResponseForMedicalPaOrders[],
) => {
  return responses.reduce(
    (acc, response) => ({
      ...response,
      hits: [...(acc.hits ?? []), ...(response.hits ?? [])],
    }),
    {} as PostgresResponseForMedicalPaOrders,
  );
};
